import React from 'react';
import { Car, Hotel, Map as MapIcon, MapPin, Pin, Plane, Target, Utensils } from 'lucide-react';
import './ItineraryPreviewCard.css';

interface ItineraryItem {
  date?: string;
  time?: string;
  name: string;
  type?: string;
  location?: string;
  duration?: string;
}

interface ItineraryPreviewData {
  title: string;
  destination?: string;
  dates: string;
  items: ItineraryItem[];
}

interface ItineraryPreviewCardProps {
  itinerary: ItineraryPreviewData;
  actionId: string;
  onConfirm: () => void;
  onViewFull?: () => void;
}

const ICON_PROPS = { size: 16, 'aria-hidden': true } as const;

// The icon is the row's only cue to the item's type (the row carries just
// time and name), so it is spoken: the span is a labelled image and the svg
// itself stays hidden.
const getTypeIcon = (type?: string): { icon: React.ReactNode; label: string } => {
  switch (type) {
    case 'travel':
    case 'flight':
      return { icon: <Plane {...ICON_PROPS} />, label: 'Flight' };
    case 'accommodation':
    case 'hotel':
    case 'checkin':
    case 'checkout':
      return { icon: <Hotel {...ICON_PROPS} />, label: 'Hotel' };
    case 'meal':
    case 'restaurant':
      return { icon: <Utensils {...ICON_PROPS} />, label: 'Meal' };
    case 'activity':
      return { icon: <Target {...ICON_PROPS} />, label: 'Activity' };
    case 'transport':
    case 'drive':
      return { icon: <Car {...ICON_PROPS} />, label: 'Transport' };
    default:
      return { icon: <Pin {...ICON_PROPS} />, label: 'Place' };
  }
};

const ItineraryPreviewCard: React.FC<ItineraryPreviewCardProps> = ({
  itinerary,
  actionId,
  onConfirm,
  onViewFull,
}) => {
  // Group items by date
  const itemsByDate = (itinerary.items || []).reduce<Record<string, ItineraryItem[]>>(
    (acc, item) => {
      const date = item.date || 'Unscheduled';
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    },
    {}
  );

  const dates = Object.keys(itemsByDate).sort();
  // Show all days in a scrollable container
  const displayDates = dates;

  return (
    <div className="itinerary-preview-card">
      <div className="itinerary-preview-header">
        <span className="itinerary-icon"><MapIcon size={28} aria-hidden="true" /></span>
        <div className="itinerary-header-text">
          <h4 className="itinerary-title">{itinerary.title}</h4>
          <span className="itinerary-dates">{itinerary.dates}</span>
        </div>
      </div>

      {itinerary.destination && (
        <div className="itinerary-destination">
          <span className="destination-icon"><MapPin size={14} role="img" aria-label="Destination" /></span>
          <span>{itinerary.destination}</span>
        </div>
      )}

      <div className="itinerary-preview-content">
        {displayDates.map((date, dateIdx) => (
          <div key={date} className="day-section">
            <div className="day-header">
              <span className="day-number">Day {dateIdx + 1}</span>
              <span className="day-date">{formatDate(date)}</span>
            </div>
            <div className="day-items">
              {itemsByDate[date].map((item, idx) => {
                const typeIcon = getTypeIcon(item.type);
                return (
                <div key={idx} className="itinerary-item">
                  <span className="item-icon" role="img" aria-label={typeIcon.label}>{typeIcon.icon}</span>
                  <div className="item-details">
                    {item.time && formatTime(item.time) && (
                      <span className="item-time">{formatTime(item.time)}</span>
                    )}
                    <span className="item-name">{item.name}</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="itinerary-preview-actions">
        {onViewFull && (
          <button className="btn btn-secondary" onClick={onViewFull}>
            View Full Itinerary
          </button>
        )}
        <button className="btn btn-primary" onClick={onConfirm}>
          Looks Good!
        </button>
      </div>
    </div>
  );
};

// Helper functions
function formatDate(dateStr: string): string {
  if (dateStr === 'Unscheduled') return dateStr;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  try {
    // Handle HH:MM format
    const parts = timeStr.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10) || 0;

    if (isNaN(hours)) return timeStr;

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  } catch {
    return timeStr;
  }
}

export default ItineraryPreviewCard;
